// Lazy loader for the vendored chart engine.
//
// insight-bi's chart classes are framework-agnostic classic scripts that
// declare globals (window.BaseVisual, window.ColumnChart, …) and read
// window.Plotly. We keep them byte-for-byte under ../vendor and execute them
// by injecting a <script> with their raw source — exactly the global-script
// model they were written for — instead of ESM-converting large third-party
// files. Plotly is pulled from npm and assigned to window once.

import { installPlotlyTheme } from './plotlyTheme'

let plotlyPromise: Promise<unknown> | null = null

/** Load Plotly once and expose it as window.Plotly (what the charts read).
 *  Every chart then draws through installPlotlyTheme's newPlot / react, which
 *  re-colours a figure drawn on a dark surface (see plotlyTheme.ts). */
export function ensurePlotly(): Promise<unknown> {
  if (typeof window === 'undefined') return Promise.resolve(null)
  const existing = (window as unknown as { Plotly?: unknown }).Plotly
  if (existing) {
    installPlotlyTheme(existing)
    return Promise.resolve(null)
  }
  if (!plotlyPromise) {
    plotlyPromise = import('plotly.js-dist-min').then((mod) => {
      const Plotly = (mod as { default?: unknown }).default ?? mod
      installPlotlyTheme(Plotly)
      ;(window as unknown as { Plotly?: unknown }).Plotly = Plotly
      return Plotly
    })
  }
  return plotlyPromise
}

// Leaflet (for the Map visual) is loaded from CDN on first use — keeps it out
// of the main bundle and avoids a hard build dependency. Resolves window.L.
let leafletPromise: Promise<unknown> | null = null

export function ensureLeaflet(): Promise<unknown> {
  if (typeof window === 'undefined') return Promise.resolve(null)
  const w = window as unknown as { L?: unknown }
  if (w.L) return Promise.resolve(w.L)
  if (!leafletPromise) {
    leafletPromise = new Promise((resolve, reject) => {
      if (!document.getElementById('leaflet-css')) {
        const link = document.createElement('link')
        link.id = 'leaflet-css'
        link.rel = 'stylesheet'
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
        document.head.appendChild(link)
      }
      const s = document.createElement('script')
      s.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
      s.onload = () => resolve(w.L)
      s.onerror = () => reject(new Error('Failed to load Leaflet (map) from CDN'))
      document.head.appendChild(s)
    })
  }
  return leafletPromise
}

// Small helper: inject a <script> once (keyed by id) and resolve on load. Used
// for Leaflet plugins that attach onto the existing window.L.
function injectCdnScript(id: string, src: string, label: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(id) as HTMLScriptElement | null
    if (existing) {
      if (existing.dataset.loaded === '1') resolve()
      else {
        existing.addEventListener('load', () => resolve())
        existing.addEventListener('error', () => reject(new Error(`Failed to load ${label} from CDN`)))
      }
      return
    }
    const s = document.createElement('script')
    s.id = id
    s.src = src
    s.onload = () => { s.dataset.loaded = '1'; resolve() }
    s.onerror = () => reject(new Error(`Failed to load ${label} from CDN`))
    document.head.appendChild(s)
  })
}

function injectCdnCss(id: string, href: string): void {
  if (document.getElementById(id)) return
  const link = document.createElement('link')
  link.id = id
  link.rel = 'stylesheet'
  link.href = href
  document.head.appendChild(link)
}

// leaflet.heat — density heatmap layer (L.heatLayer). Depends on Leaflet, so we
// ensureLeaflet() first, then attach the plugin. Resolves window.L (now with
// L.heatLayer defined).
let leafletHeatPromise: Promise<unknown> | null = null

export function ensureLeafletHeat(): Promise<unknown> {
  if (typeof window === 'undefined') return Promise.resolve(null)
  const w = window as unknown as { L?: { heatLayer?: unknown } }
  if (!leafletHeatPromise) {
    leafletHeatPromise = ensureLeaflet()
      .then(() => {
        if (w.L?.heatLayer) return
        return injectCdnScript(
          'leaflet-heat-js',
          'https://unpkg.com/leaflet.heat@0.2.0/dist/leaflet-heat.js',
          'leaflet.heat (heatmap)',
        )
      })
      .then(() => w.L)
      .catch((err) => { leafletHeatPromise = null; throw err })
  }
  return leafletHeatPromise
}

// leaflet.markercluster — groups dense markers into count bubbles that expand on
// zoom (L.markerClusterGroup). Depends on Leaflet + its own CSS. Resolves
// window.L (now with L.markerClusterGroup defined).
let leafletClusterPromise: Promise<unknown> | null = null

export function ensureLeafletCluster(): Promise<unknown> {
  if (typeof window === 'undefined') return Promise.resolve(null)
  const w = window as unknown as { L?: { markerClusterGroup?: unknown } }
  if (!leafletClusterPromise) {
    leafletClusterPromise = ensureLeaflet()
      .then(() => {
        if (w.L?.markerClusterGroup) return
        injectCdnCss('leaflet-cluster-css', 'https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css')
        injectCdnCss('leaflet-cluster-default-css', 'https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.Default.css')
        return injectCdnScript(
          'leaflet-cluster-js',
          'https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js',
          'leaflet.markercluster (clustering)',
        )
      })
      .then(() => w.L)
      .catch((err) => { leafletClusterPromise = null; throw err })
  }
  return leafletClusterPromise
}

// ProPivot (@proteus/propivot) — the open-source pivot engine that replaces
// WebDataRocks here. Its source lives in its OWN sibling project (../ProPivot);
// for this repo it is an EXTERNAL library: the '@proteus/propivot' specifier is
// aliased (in each consumer's vite.config) to that project's built dist, so we
// never vendor its source. It is a drop-in for WebDataRocks — the same report /
// config shape (dataSource + slice + measures + options + conditions) and the
// same `new Pivot({ container, toolbar, report, reportcomplete })` constructor —
// so PivotVisual builds one report and swaps only the constructor.
//
// Loaded via a lazy dynamic import so the ~250 KB engine stays out of the main
// bundle and splits into its own async chunk; the stylesheet rides along in the
// same chunk. Resolves the ProPivot constructor.
let proPivotPromise: Promise<unknown> | null = null

export function ensureProPivot(): Promise<unknown> {
  if (typeof window === 'undefined') return Promise.resolve(null)
  if (!proPivotPromise) {
    proPivotPromise = (async () => {
      await import('@proteus/propivot/propivot.css')
      const mod = (await import('@proteus/propivot')) as {
        ProPivot?: unknown
        default?: unknown
      }
      return mod.ProPivot ?? mod.default ?? null
    })().catch((err) => {
      // Reset so a later render can retry; surface the failure to the caller.
      proPivotPromise = null
      throw err
    })
  }
  return proPivotPromise
}

// ProPivot's ENGINE half (LocalEngine / buildStore / buildMatrix) — the headless
// columnar store + slice planner, with no grid, facade or stylesheet. Used by
// the dashboard in-memory model (visualModel.ts) to fold each visual out of one
// shared dataset. Same alias as ensureProPivot, so both resolve to the one
// built copy of the sibling project and share its chunk.
let proPivotEnginePromise: Promise<unknown> | null = null

export function ensureProPivotEngine(): Promise<unknown> {
  if (typeof window === 'undefined') return Promise.resolve(null)
  if (!proPivotEnginePromise) {
    proPivotEnginePromise = import('@proteus/propivot').catch((err) => {
      // Reset so a later render can retry; surface the failure to the caller.
      proPivotEnginePromise = null
      throw err
    })
  }
  return proPivotEnginePromise
}

// Dynamic raw-source loaders, one per vendored file. Dynamic import keeps each
// chart's source in its own lazily-fetched chunk. Add new entries as more
// chart types are vendored (Phase 2+).
const VENDOR_SOURCES: Record<string, () => Promise<string>> = {
  BaseVisual: async () => (await import('../vendor/BaseVisual.js?raw')).default,
  ColumnChart: async () => (await import('../vendor/ColumnChart.js?raw')).default,
  BarChart: async () => (await import('../vendor/BarChart.js?raw')).default,
  LineChart: async () => (await import('../vendor/LineChart.js?raw')).default,
  PieChart: async () => (await import('../vendor/PieChart.js?raw')).default,
  DoughnutChart: async () => (await import('../vendor/DoughnutChart.js?raw')).default,
  ScatterPlot: async () => (await import('../vendor/ScatterPlot.js?raw')).default,
  StackedColumnChart: async () => (await import('../vendor/StackedColumnChart.js?raw')).default,
  ColumnLineChart: async () => (await import('../vendor/ColumnLineChart.js?raw')).default,
  BubbleChart: async () => (await import('../vendor/BubbleChart.js?raw')).default,
  Heatmap: async () => (await import('../vendor/Heatmap.js?raw')).default,
  GaugeChart: async () => (await import('../vendor/Gauge.js?raw')).default,
  TreemapChart: async () => (await import('../vendor/Treemap.js?raw')).default,
  FunnelChart: async () => (await import('../vendor/Funnel.js?raw')).default,
  WaterfallChart: async () => (await import('../vendor/Waterfall.js?raw')).default,
  RadarChart: async () => (await import('../vendor/Radar.js?raw')).default,
  SankeyChart: async () => (await import('../vendor/Sankey.js?raw')).default,
  // Gantt / task scheduler — insight-bi's vanilla-JS class, vendored verbatim
  // with a window export appended. Defines window.TaskScheduler.
  TaskScheduler: async () => (await import('../vendor/taskScheduler.js?raw')).default,
}

const injected = new Set<string>()

function injectScript(globalName: string, source: string): void {
  const id = `viz-vendor-${globalName}`
  if (injected.has(globalName) || document.getElementById(id)) {
    injected.add(globalName)
    return
  }
  const el = document.createElement('script')
  el.id = id
  el.type = 'text/javascript'
  el.textContent = source
  document.head.appendChild(el)
  injected.add(globalName)
}

/**
 * Ensure the named vendored globals are loaded, in order. Each name maps to a
 * vendored file that defines window[name]; already-present globals are skipped.
 */
export async function ensureVendor(globalNames: string[]): Promise<void> {
  if (typeof window === 'undefined') return
  for (const name of globalNames) {
    if ((window as unknown as Record<string, unknown>)[name]) {
      injected.add(name)
      continue
    }
    const loader = VENDOR_SOURCES[name]
    if (!loader) {
      throw new Error(`visual-engine: no vendored source registered for "${name}"`)
    }
    injectScript(name, await loader())
  }
}

/** Resize the Plotly chart inside a host container (best-effort). */
export function resizeChart(container: HTMLElement | null): void {
  const Plotly = (window as unknown as { Plotly?: { Plots?: { resize?: (d: Element) => void } } }).Plotly
  if (!container || !Plotly?.Plots?.resize) return
  // The Plotly graph div itself — a chart may wrap it (the heatmap puts it in
  // a scroll box), so the container's first <div> is not necessarily it.
  const div = container.querySelector('.js-plotly-plot') || container.querySelector('div')
  if (div) {
    try {
      Plotly.Plots.resize(div)
    } catch {
      /* resize is best-effort; ignore transient Plotly errors */
    }
  }
}
